/**
 * React Query hooks for the damage-analysis API.
 *
 * Backend routes are under /api/0/reservation/:id/damage-analysis*.
 * The mutation runs the analysis (slow: 20–60 s, calls Gemini); the
 * read queries are cheap (just activity-log lookups).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { get, post } from '../fetch'

export type DamageType =
	| 'scratch'
	| 'dent'
	| 'scuff'
	| 'crack'
	| 'chip'
	| 'missing_part'
	| 'dirt_stain'
	| 'other'

export type DamageAngle =
	| 'front'
	| 'left_side'
	| 'rear'
	| 'right_side'
	| 'interior'
	| 'trunk'
	| 'extra'
	| ''

export type DamageItem = {
	location: string
	angle: DamageAngle
	type: DamageType
	severity: 1 | 2 | 3
	confidence: number
	note: string
}

export type DamageReport = {
	version: number
	reservation_id: string
	model: string
	model_primary: string
	model_fallback_used: boolean
	model_attempts: Array<{
		model: string
		key_index: number
		status: number
		reason: string
	}>
	key_index: number
	created_at: string
	duration_ms: number
	counts: {
		before_total: number
		before_loaded: number
		after_total: number
		after_loaded: number
	}
	min_confidence_filter: number
	source_request_ids: { handover: string; return: string }
	summary: string
	damages: DamageItem[]
	activity_log_id?: string
	logged_at?: string
}

export type DamageAvailability = {
	hasHandover: boolean
	hasReturn: boolean
	beforePhotoCount: number
	afterPhotoCount: number
	canRun: boolean
	hasReport: boolean
	lastReportAt: string | null
}

const baseUrl = (reservationId: string) =>
	`/api/0/reservation/${encodeURIComponent(reservationId)}/damage-analysis`

const queryKeys = {
	availability: (id: string) => ['api', 'damage-analysis', 'availability', id] as const,
	report: (id: string) => ['api', 'damage-analysis', 'report', id] as const,
}

/**
 * Lightweight check used to decide whether the 'Vyhodnotit poškození'
 * tab should appear. Always returns shape — even when canRun is false.
 */
export function useDamageAnalysisAvailability(reservationId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.availability(reservationId),
		queryFn: () => get<DamageAvailability>(`${baseUrl(reservationId)}/availability`),
		enabled: enabled && !!reservationId,
		// Refetch on focus so the tab appears as soon as workflow requests arrive.
		refetchOnWindowFocus: true,
		staleTime: 30_000,
	})
}

/**
 * Latest stored report (or 404 → null). Cheap to call repeatedly.
 */
export function useDamageReport(reservationId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.report(reservationId),
		queryFn: async () => {
			try {
				return await get<DamageReport>(baseUrl(reservationId))
			} catch (err: unknown) {
				// 404 — no report yet — treat as null instead of failure.
				const error = err as { code?: string; message?: string }
				if (error?.code === 'no_report' || /404/.test(error?.message || '')) {
					return null
				}
				throw err
			}
		},
		enabled: enabled && !!reservationId,
		staleTime: 60_000,
	})
}

/**
 * Run the analysis. Long-running. Invalidates the availability + report
 * queries on success so the UI re-fetches.
 */
export function useDamageAnalysisRun(reservationId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (variables: { minConfidence?: number }) =>
			post<DamageReport>(baseUrl(reservationId), {
				min_confidence: variables.minConfidence ?? 0,
			}),
		onSuccess: report => {
			queryClient.setQueryData(queryKeys.report(reservationId), report)
			queryClient.invalidateQueries({ queryKey: queryKeys.availability(reservationId) })
		},
	})
}
